import React, { useEffect, useId, useRef, useState } from 'react';
import { filtrarOpciones } from '../lib/sso-documentos';

export interface OpcionBuscador { value: string; label: string }

// Campo con buscador (bloque 5d, A3): se escribe y se filtran las opciones sin
// distinguir tildes ni mayúsculas. Patrón "combobox" de WAI-ARIA: flechas para
// moverse, Enter para elegir, Esc para cerrar sin cambiar; con el dedo o el
// mouse se toca la opción. Solo acepta una de las opciones (no texto libre).
export default function BuscadorOpciones({ opciones, value, onChange, placeholder = 'Escribe para buscar…', inputStyle, ariaLabel, disabled }: {
  opciones: OpcionBuscador[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputStyle?: React.CSSProperties;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const id = useId();
  const listaId = `${id}-lista`;
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [activo, setActivo] = useState(0);
  const listaRef = useRef<HTMLUListElement>(null);
  const cierre = useRef<ReturnType<typeof setTimeout> | null>(null);

  const elegida = opciones.find(o => o.value === value) ?? null;
  const visibles = filtrarOpciones(opciones, consulta);

  useEffect(() => () => { if (cierre.current) clearTimeout(cierre.current); }, []);

  // La opción activa queda a la vista al moverse con las flechas.
  useEffect(() => {
    if (!abierto) return;
    const el = listaRef.current?.children[activo] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activo, abierto]);

  function abrir() {
    if (cierre.current) { clearTimeout(cierre.current); cierre.current = null; }
    if (abierto) return;
    setConsulta('');
    const i = opciones.findIndex(o => o.value === value);
    setActivo(i >= 0 ? i : 0);
    setAbierto(true);
  }

  function cerrar() {
    setAbierto(false);
    setConsulta('');
  }

  function elegir(o: OpcionBuscador) {
    if (cierre.current) { clearTimeout(cierre.current); cierre.current = null; }
    onChange(o.value);
    cerrar();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!abierto) { abrir(); return; }
      if (visibles.length === 0) return;
      const paso = e.key === 'ArrowDown' ? 1 : -1;
      setActivo(a => (a + paso + visibles.length) % visibles.length);
    } else if (e.key === 'Enter') {
      if (!abierto) return;
      e.preventDefault();
      const o = visibles[activo];
      if (o) elegir(o);
    } else if (e.key === 'Escape') {
      if (abierto) { e.preventDefault(); e.stopPropagation(); cerrar(); }
    } else if (e.key === 'Tab') {
      cerrar();
    }
  }

  const activoId = abierto && visibles[activo] ? `${id}-op-${activo}` : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={abierto}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-activedescendant={activoId}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        style={inputStyle}
        placeholder={abierto ? (elegida?.label ?? placeholder) : placeholder}
        value={abierto ? consulta : (elegida?.label ?? '')}
        onFocus={abrir}
        onClick={abrir}
        onChange={e => { if (!abierto) abrir(); setConsulta(e.target.value); setActivo(0); }}
        onKeyDown={onKeyDown}
        // Con el dedo, en algunos navegadores el campo pierde el foco antes del
        // clic en la opción: se cierra un instante después para no perderlo.
        onBlur={() => { cierre.current = setTimeout(cerrar, 150); }}
      />
      {abierto && (
        <ul
          ref={listaRef}
          id={listaId}
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
            margin: 0, padding: 4, listStyle: 'none', maxHeight: 240, overflowY: 'auto',
            background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
            boxShadow: '0 12px 24px rgba(0,0,0,0.45)',
          }}
        >
          {visibles.length === 0 ? (
            <li style={{ padding: '10px 12px', color: '#64748b', fontSize: '0.82rem' }}>Sin resultados</li>
          ) : visibles.map((o, i) => (
            <li
              key={o.value}
              id={`${id}-op-${i}`}
              role="option"
              aria-selected={o.value === value}
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setActivo(i)}
              onClick={() => elegir(o)}
              style={{
                padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '0.86rem',
                color: o.value === value ? '#2FBF71' : '#e2e8f0',
                fontWeight: o.value === value ? 700 : 400,
                background: i === activo ? 'rgba(96,165,250,0.15)' : 'transparent',
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
